import React from "react";
import { Typography, Box, Tabs, Tab } from "@material-ui/core";
import { useQuestionnairePreview } from "../../queries";
import type { ResolvedQuestionnaire, PreviewAttrs } from "../../api";
import {
  ResolvedQuestionPreview,
  ConditionalPart,
  ValidationContext,
  PageContext,
  FeelContext,
  RespondedContext,
  evaluateFeelPredicate,
  computeRevealedQuestions,
  collectQuestionnaireFeelSeed,
} from "./ResolvedComponents";
import { PatientPreviewShell } from "./PatientPreviewShell";
import LinkifiedText from "./LinkifiedText";
import { previewStyles as styles } from "./previewStyles";
import type { Page, Part } from "../../types/resolved";
import type { Visibility, Requirement } from "../../types/common";
import { QuestionnaireDocument } from "./QuestionnaireDocument";
import { oneOf, useViewState } from "utils/useViewState";

const PREVIEW_VIEWS = ["preview", "document"] as const;

const flattenPageQuestionRefs = (parts: ReadonlyArray<Part>, feelCtx: Record<string, unknown>) => {
  const refs: Array<{ questionId: number; visibility: Visibility; requirement?: Requirement }> = [];
  for (const part of parts) {
    if (part._type === "question" && part.question) {
      refs.push({ questionId: part.question.id, visibility: part.visibility, requirement: part.requirement });
    }
    if (part._type === "question_group" && part.question_group) {
      const groupVisible = part.visibility === "visible" || evaluateFeelPredicate(part.visibility.visibleWhen, feelCtx);
      if (groupVisible) {
        for (const qRef of part.question_group.questions) {
          if (qRef.question) {
            refs.push({ questionId: qRef.question.id, visibility: qRef.visibility, requirement: qRef.requirement });
          }
        }
      }
    }
  }
  return refs;
};

const RevealedPage: React.VFC<{ page: Page }> = ({ page }) => {
  const feelContext = React.useContext(FeelContext);
  const respondedIds = React.useContext(RespondedContext);

  const flatRefs = React.useMemo(() => flattenPageQuestionRefs(page.parts, feelContext), [page.parts, feelContext]);
  const revealedIds = React.useMemo(
    () => computeRevealedQuestions(flatRefs, feelContext, respondedIds),
    [flatRefs, feelContext, respondedIds]
  );

  return (
    <>
      {page.parts.map((part, partIdx) => {
        if (part._type === "question" && part.question) {
          if (!revealedIds.has(part.question.id)) return null;
          return (
            <ConditionalPart key={partIdx} visibility={part.visibility} requirement={part.requirement}>
              {isRequired => <ResolvedQuestionPreview question={part.question} isRequired={isRequired} />}
            </ConditionalPart>
          );
        }
        if (part._type === "question_group" && part.question_group) {
          const group = part.question_group;
          const hasRevealed = group.questions.some(qRef => qRef.question && revealedIds.has(qRef.question.id));
          if (!hasRevealed) return null;
          return (
            <ConditionalPart key={partIdx} visibility={part.visibility}>
              {() => (
                <Box className={styles.questionGroupInQuestionnaire}>
                  {group.title && (
                    <Typography variant="h6" className={styles.pageTitle}>
                      {group.title}
                    </Typography>
                  )}
                  {group.description && (
                    <Typography variant="body2" className={styles.groupDescription}>
                      <LinkifiedText text={group.description} />
                    </Typography>
                  )}
                  {group.questions.map((qRef, qi) =>
                    qRef.question && revealedIds.has(qRef.question.id) ? (
                      <ConditionalPart key={qi} visibility={qRef.visibility} requirement={qRef.requirement}>
                        {isRequired => <ResolvedQuestionPreview question={qRef.question} isRequired={isRequired} />}
                      </ConditionalPart>
                    ) : null
                  )}
                </Box>
              )}
            </ConditionalPart>
          );
        }
        return null;
      })}
    </>
  );
};

export const ResolvedQuestionnaireContent: React.VFC<{
  questionnaire: ResolvedQuestionnaire;
  currentPageIndex: number;
  onPageIndexChange: (index: number) => void;
}> = ({ questionnaire, currentPageIndex, onPageIndexChange }) => {
  const { validate } = React.useContext(ValidationContext);

  const handleNext = () => {
    if (validate(currentPageIndex)) {
      onPageIndexChange(currentPageIndex + 1);
    }
  };

  return (
    <div className={styles.preview}>
      <div className={styles.pageNav}>
        {questionnaire.pages.map((page, idx) => (
          <button
            key={idx}
            className={`${styles.pageNavButton} ${idx === currentPageIndex ? styles.pageNavButtonActive : ""}`}
            onClick={() => onPageIndexChange(idx)}
            title={page.title || "Untitled"}
          >
            {idx + 1}
          </button>
        ))}
      </div>

      {questionnaire.pages.map((page, pageIdx) => (
        <PageContext.Provider key={pageIdx} value={pageIdx}>
          <div style={{ display: pageIdx === currentPageIndex ? undefined : "none" }}>
            <Typography variant="h6" className={styles.pageTitle}>
              {page.title || "Untitled"}
            </Typography>

            <RevealedPage page={page} />
          </div>
        </PageContext.Provider>
      ))}

      {questionnaire.pages.length > 0 && (
        <div className={styles.pageNavButtons}>
          <button
            className={styles.navButton}
            onClick={() => onPageIndexChange(currentPageIndex - 1)}
            disabled={currentPageIndex === 0}
          >
            ← Previous
          </button>
          <span className={styles.pageIndicator}>
            Page {currentPageIndex + 1} of {questionnaire.pages.length}
          </span>
          <button
            className={styles.navButton}
            onClick={handleNext}
            disabled={currentPageIndex >= questionnaire.pages.length - 1}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

const QuestionnairePreview: React.VFC<{
  id: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  currentPageIndex?: number;
  onPageIndexChange?: (index: number) => void;
  attrs: PreviewAttrs;
  onAttrsChange: (attrs: PreviewAttrs) => void;
}> = ({ id, isExpanded, onToggleExpand, currentPageIndex, onPageIndexChange, attrs, onAttrsChange }) => {
  const resolvedPageIndex = currentPageIndex ?? 0;
  const setResolvedPageIndex = onPageIndexChange ?? (() => {});

  const { data: previewData, isLoading, error } = useQuestionnairePreview(id, attrs);

  const [{ preview_view }, setView] = useViewState({ preview_view: "preview" });
  const view = oneOf<string>(PREVIEW_VIEWS, preview_view, "preview");

  const seedFeelContext = React.useMemo(
    () => (previewData?.questionnaire ? collectQuestionnaireFeelSeed(previewData.questionnaire) : undefined),
    [previewData]
  );

  return (
    <PatientPreviewShell
      isExpanded={isExpanded}
      onToggleExpand={onToggleExpand}
      isLoading={isLoading}
      error={error}
      attrs={attrs}
      seedFeelContext={seedFeelContext}
      onApplyAttrs={next => {
        onAttrsChange(next);
        setResolvedPageIndex(0);
      }}
    >
      <Tabs value={view} onChange={(_, v) => setView({ preview_view: v })} indicatorColor="primary" textColor="primary">
        <Tab label="Preview" value="preview" />
        <Tab label="Document" value="document" />
      </Tabs>
      {view === "preview" && previewData?.questionnaire && (
        <ResolvedQuestionnaireContent
          questionnaire={previewData.questionnaire}
          currentPageIndex={resolvedPageIndex}
          onPageIndexChange={setResolvedPageIndex}
        />
      )}
      {view === "document" && previewData?.questionnaire && (
        <QuestionnaireDocument questionnaire={previewData.questionnaire} currentPageIndex={resolvedPageIndex} />
      )}
    </PatientPreviewShell>
  );
};

export default QuestionnairePreview;
